import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import CaseStudyService, {
  CaseStudy,
  CaseStudyInput,
  InstructorCourse,
} from "../../_services/CaseStudyService";
import "./CaseEditorModal.css";

interface CaseEditorModalProps {
  isOpen: boolean;
  existing: CaseStudy | null;
  onClose: () => void;
  onSaved: (saved: CaseStudy | null) => void;
  initialAiTopic?: string;
  autoOpenAi?: boolean;
  courses?: InstructorCourse[];
  courseMode?: "instructor" | "admin";
  initialCourseId?: number | null;
}

function parseTagList(text: string): string[] {
  return text
    .split(/[,\n]/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

const AGE_BANDS = [
  { value: "primary", label: "Primary (gr 1-5)" },
  { value: "middle", label: "Middle (gr 6-8)" },
  { value: "secondary", label: "Secondary (gr 9-10)" },
  { value: "higher-secondary", label: "Higher secondary (gr 11-12)" },
  { value: "tertiary", label: "Tertiary / UG" },
];

const DIFFICULTY_LEVELS = [
  { value: "easy", label: "Easy" },
  { value: "moderate", label: "Moderate" },
  { value: "hots", label: "HOTS / advanced" },
];

const CaseEditorModal: React.FC<CaseEditorModalProps> = ({
  isOpen,
  existing,
  onClose,
  onSaved,
  initialAiTopic,
  autoOpenAi,
  courses = [],
  courseMode = "admin",
  initialCourseId = null,
}) => {
  const [title, setTitle] = useState("");
  const [caseText, setCaseText] = useState("");
  const [learningObjectives, setLearningObjectives] = useState("");
  const [keyConceptsText, setKeyConceptsText] = useState("");
  const [misconceptionsText, setMisconceptionsText] = useState("");
  const [courseId, setCourseId] = useState<number | "">("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // AI draft panel state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiSubject, setAiSubject] = useState("");
  const [aiAgeBand, setAiAgeBand] = useState("secondary");
  const [aiDifficulty, setAiDifficulty] = useState("moderate");
  const [aiFocus, setAiFocus] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiDraftActive, setAiDraftActive] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (existing) {
      setTitle(existing.Title);
      setCaseText(existing.CaseText);
      setLearningObjectives(existing.LearningObjectives || "");
      setKeyConceptsText((existing.KeyConcepts || []).join(", "));
      setMisconceptionsText((existing.ExpectedMisconceptions || []).join("\n"));
      setCourseId(existing.CourseId ?? "");
    } else {
      setTitle("");
      setCaseText("");
      setLearningObjectives("");
      setKeyConceptsText("");
      setMisconceptionsText("");
      setCourseId(initialCourseId ?? "");
    }
    const seedTopic = !existing && initialAiTopic ? initialAiTopic : "";
    setAiOpen(!existing && !!autoOpenAi);
    setAiTopic(seedTopic);
    setAiSubject("");
    setAiAgeBand("secondary");
    setAiDifficulty("moderate");
    setAiFocus("");
    setAiDraftActive(false);
    setAiError(null);
    setError(null);
  }, [isOpen, existing, initialAiTopic, autoOpenAi, initialCourseId]);

  const handleSave = async () => {
    if (!title.trim() || !caseText.trim()) {
      setError("Title and case text are required.");
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const payload: CaseStudyInput = {
        title: title.trim(),
        caseText: caseText.trim(),
        learningObjectives: learningObjectives.trim() || undefined,
        keyConcepts: parseTagList(keyConceptsText),
        expectedMisconceptions: parseTagList(misconceptionsText),
        courseId: courseId === "" ? null : Number(courseId),
      };
      let savedCase: CaseStudy;
      if (existing) {
        const resp = await CaseStudyService.update(existing.Id, payload);
        savedCase = resp.data;
      } else {
        const resp = await CaseStudyService.create(payload);
        savedCase = resp.data;
      }
      onSaved(savedCase);
    } catch (e: any) {
      setError(e?.message || "Failed to save case");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!aiTopic.trim()) {
      setAiError("Tell me what the case should cover (a short phrase is enough).");
      return;
    }
    try {
      setAiGenerating(true);
      setAiError(null);
      const resp = await CaseStudyService.generate({
        topic: aiTopic.trim(),
        subject: aiSubject.trim() || undefined,
        ageBand: aiAgeBand || undefined,
        difficulty: aiDifficulty || undefined,
        focus: aiFocus.trim() || undefined,
      });
      if (resp.success) {
        setTitle(resp.data.title || "");
        setCaseText(resp.data.caseText || "");
        setLearningObjectives(resp.data.learningObjectives || "");
        setKeyConceptsText((resp.data.keyConcepts || []).join(", "));
        setMisconceptionsText((resp.data.expectedMisconceptions || []).join("\n"));
        setAiDraftActive(true);
        setAiOpen(false);
      }
    } catch (e: any) {
      const serverMsg = e?.response?.data?.message;
      const serverError = e?.response?.data?.error;
      const fallback = e?.message || "AI couldn't draft this — try a more specific topic or author manually.";
      setAiError(
        serverError && serverMsg
          ? `${serverMsg} (${serverError})`
          : serverMsg || fallback,
      );
    } finally {
      setAiGenerating(false);
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      size="lg"
      dialogClassName="case-editor-modal-dialog"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>{existing ? "Edit Case" : "New Case"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {!existing && (
          <div className={`ce-ai-panel ${aiOpen ? "is-open" : ""}`}>
            <button
              type="button"
              className="ce-ai-header"
              onClick={() => setAiOpen((v) => !v)}
            >
              <span className="ce-ai-icon">✨</span>
              <div className="ce-ai-header-text">
                <strong>Draft with AI</strong>
                <span className="ce-ai-subtitle">
                  Give me a topic and I'll pre-fill every field. You review and edit.
                </span>
              </div>
              <span className="ce-ai-caret">{aiOpen ? "▲" : "▼"}</span>
            </button>

            {aiOpen && (
              <div className="ce-ai-body">
                {aiError && <div className="ce-ai-error">{aiError}</div>}
                <Form.Group className="ce-ai-field">
                  <Form.Label>
                    What should this case cover? <span className="ce-required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={aiTopic}
                    onChange={(e) => setAiTopic(e.target.value)}
                    placeholder="A short phrase describing what the case should cover"
                    disabled={aiGenerating}
                  />
                </Form.Group>
                <div className="ce-ai-row">
                  <Form.Group className="ce-ai-field ce-ai-field-half">
                    <Form.Label>Subject</Form.Label>
                    <Form.Control
                      type="text"
                      value={aiSubject}
                      onChange={(e) => setAiSubject(e.target.value)}
                      placeholder="E.g., Biology, Economics, History"
                      disabled={aiGenerating}
                    />
                  </Form.Group>
                  <Form.Group className="ce-ai-field ce-ai-field-quarter">
                    <Form.Label>Age band</Form.Label>
                    <Form.Select
                      value={aiAgeBand}
                      onChange={(e) => setAiAgeBand(e.target.value)}
                      disabled={aiGenerating}
                    >
                      {AGE_BANDS.map((b) => (
                        <option key={b.value} value={b.value}>
                          {b.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="ce-ai-field ce-ai-field-quarter">
                    <Form.Label>Difficulty</Form.Label>
                    <Form.Select
                      value={aiDifficulty}
                      onChange={(e) => setAiDifficulty(e.target.value)}
                      disabled={aiGenerating}
                    >
                      {DIFFICULTY_LEVELS.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <Form.Group className="ce-ai-field">
                  <Form.Label>
                    Anything specific to weave in? <span className="ce-hint">optional</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={aiFocus}
                    onChange={(e) => setAiFocus(e.target.value)}
                    placeholder="A constraint, setting, or angle to weave in"
                    disabled={aiGenerating}
                  />
                </Form.Group>
                <div className="ce-ai-actions">
                  <Button
                    onClick={handleGenerate}
                    disabled={aiGenerating || !aiTopic.trim()}
                    className="ce-ai-gen-btn"
                  >
                    {aiGenerating ? (
                      <>
                        <Spinner size="sm" animation="border" /> Drafting…
                      </>
                    ) : (
                      <>✨ Draft this case</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {aiDraftActive && (
          <div className="ce-ai-draft-banner">
            <span className="ce-ai-draft-pill">AI draft</span>
            <span>
              Review every field before saving. The AI is good at openers, less good at edge cases —
              you know your students.
            </span>
            <button
              type="button"
              className="ce-ai-draft-dismiss"
              onClick={() => setAiDraftActive(false)}
              title="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        <Form.Group className="ce-field">
          <Form.Label>Title <span className="ce-required">*</span></Form.Label>
          <Form.Control
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
            placeholder="A short, specific title for the case"
          />
        </Form.Group>

        {courses.length > 0 && (
          <Form.Group className="ce-field">
            <Form.Label>
              Course
              <span className="ce-hint">
                {courseMode === "instructor"
                  ? "which of your courses does this case belong to?"
                  : "which course does this case belong to?"}
              </span>
            </Form.Label>
            <Form.Select
              value={courseId}
              onChange={(e) =>
                setCourseId(e.target.value ? Number(e.target.value) : "")
              }
            >
              <option value="">— Not tied to a specific course —</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.shortCode ? `${c.shortCode} — ${c.name}` : c.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        <Form.Group className="ce-field">
          <Form.Label>Case scenario <span className="ce-required">*</span></Form.Label>
          <Form.Control
            as="textarea"
            rows={8}
            value={caseText}
            onChange={(e) => setCaseText(e.target.value)}
            placeholder="Write the scenario students will analyse. Enough context to reason about it, but leave room for inquiry."
          />
        </Form.Group>

        <Form.Group className="ce-field">
          <Form.Label>Learning objectives</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={learningObjectives}
            onChange={(e) => setLearningObjectives(e.target.value)}
            placeholder="What should students grasp by the end of the discussion?"
          />
        </Form.Group>

        <Form.Group className="ce-field">
          <Form.Label>
            Key concepts
            <span className="ce-hint">comma-separated</span>
          </Form.Label>
          <Form.Control
            type="text"
            value={keyConceptsText}
            onChange={(e) => setKeyConceptsText(e.target.value)}
            placeholder="The 5–10 concepts the case touches on"
          />
        </Form.Group>

        <Form.Group className="ce-field">
          <Form.Label>
            Expected misconceptions
            <span className="ce-hint">one per line — used by AI to spot common confusions</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={misconceptionsText}
            onChange={(e) => setMisconceptionsText(e.target.value)}
            placeholder={"Common wrong-but-tempting answers students reach for, one per line"}
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner size="sm" animation="border" /> Saving…
            </>
          ) : existing ? (
            "Save changes"
          ) : (
            "Create case"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CaseEditorModal;
